import { useMutation, useQuery } from "@apollo/client";
import React, { Fragment, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Spinner } from "@nextui-org/react";
import Timeline from "../Timeline/Timeline";
import PUBLIC_SERVANT from "./queries/publicServant";
import InstitutionName from "../ui/InstitutionName/InstitutionName";
import InstitutionChip from "../ui/InstitutionChip/InstitutionChip";
import PublicServantConnections from "./PublicServantConnections/PublicServantConnections";
import useFollow, { getIsFollowing, useUnfollow } from "../../utils/followUtils";
import LoadingSpinner from "../ui/LoadingSpinner/LoadingSpinner";
import ProfileLoading from "../ui/ProfileLoading/ProfileLoading";
import StackedBarChart from "../charts/StackedBarChart/StackedBarChart";
import { useDispatch, useSelector } from "react-redux";
import { currentUser } from "../../store/selectors/auth";
import { openSignUpModal, setSidePeek } from "../../store/slices/uiSlice";
import { checkHasViewedProfile, countProfileView } from "../../utils/authUtils";
import { addProfileView } from "../../store/slices/authSlice";
import TreeDiagram from "../charts/TreeDiagram/TreeDiagram";
import ProfileHeadline from "../ui/ProfileHeadline/ProfileHeadline";
import SEO from "../ui/SEO/SEO";
import Footer from "../ui/Footer/Footer";
import PageViews from "../ui/PageViews/PageViews";
import { PAGE_H2 } from "../../utils/formatters";

const PublicServantProfile = ({

}) => {

    const [searchParams, setSearchParams] = useSearchParams();
    const name = searchParams.get('name');

    const dispatch = useDispatch();
    const activeUser = useSelector(currentUser);

    const onOpenSidePeek = () => dispatch(setSidePeek({
        open: true,
        template: `PUBLIC_SERVANT_COMPANIES`,
        publicServantGivenName: name
    }));

    const { loading, error, data, fetchMore } = useQuery(PUBLIC_SERVANT, {
        variables: {
            publicServant: name,
            timelineOffset: 0
        },
        fetchPolicy: 'cache-and-network'
    });

    // count profile view for free users
    useEffect(() => {
        const hasViewedProfile = checkHasViewedProfile(name);
        if (!hasViewedProfile) {
            countProfileView(name);
            dispatch(addProfileView());
        }
    }, [])

    const sidePeekIsOpen = useSelector(state => state.ui.sidePeek.open);

    const [follow, { followLoading, followError, followData }] = useFollow();
    const [unfollow, { unfollowLoading, unfollowError, unfollowData }] = useUnfollow();

    const onFollow = () => {

        if (!activeUser) {
            dispatch(openSignUpModal());
        }

        if (activeUser) {
            follow({
                variables: {
                    name: name,
                    entityType: 'PUBLIC_SERVANT'
                }
            })
        }
    }

    const onUnfollow = () => {
        unfollow({
            variables: {
                name: name,
                entityType: 'PUBLIC_SERVANT'
            }
        })
    }

    const onFetchMore = () => {
        fetchMore({
            variables: {
                timelineOffset: data.timeline.length
            }
        })
    }

    const isFollowing = getIsFollowing(name, `PUBLIC_SERVANT`);

    if (loading) {
        return (
            <Fragment>
                <ProfileLoading />
            </Fragment>
        )
    }

    return (
        <div className="mt-0 mb-16 mx-4 sm:mx-0">
            <ProfileHeadline
                name={name}
            />
            <div className="flex gap-3 md:items-center">
                {!isFollowing && (
                    <Button size="sm"
                        className="font-medium skeuo-btn text-white w-16"
                        onClick={onFollow}
                        isLoading={followLoading}
                        disableAnimation
                        disableRipple
                    >
                        {followLoading ? '' : 'Follow'}
                    </Button>
                )}
                {isFollowing && (
                    <Button size="sm"
                        className="font-medium text-black w-20"
                        variant="flat"
                        disableAnimation
                        disableRipple
                        isLoading={unfollowLoading}
                        onClick={onUnfollow}
                    >
                        {unfollowLoading ? '' : 'Following'}
                    </Button>
                )}
                <PageViews count={data.pageViews} />
                <p className="hidden sm:flex flex-col gap-2 md:flex-row md:items-center font-medium text-dark-grey">
                    <span className="md:truncate md:max-w-lg inline-block">
                        <span>{data.official.title}, {data.official.branch ? `${data.official.branch}, ` :''}</span>
                        <Link 
                            to={`/institution/?name=${encodeURIComponent(data.official.institution)}`}
                            className="text-dark-grey"
                        >
                            <InstitutionName fullName={data.official.institution} />
                        </Link> 
                    </span>
                </p>
            </div>

            <div className="sm:hidden my-3">
                <p className="flex flex-col gap-2 md:flex-row md:items-center font-medium text-dark-grey">
                    <span className="md:truncate md:max-w-lg inline-block">
                        <span>{data.official.title}, {data.official.branch ? `${data.official.branch}, ` :''}</span>
                        <Link 
                            to={`/institution/?name=${encodeURIComponent(data.official.institution)}`}
                            className="text-dark-grey"
                        >
                            <InstitutionName fullName={data.official.institution} />
                        </Link> 
                    </span>
                </p>
            </div>

            <div className="flex relative mt-8 mb-6 hidden sm:block">
                <TreeDiagram 
                    name={name}
                    connections={data.relationships}
                    totalRelationships={data.totalRelationships}
                    seeAllHandler={onOpenSidePeek}
                    linkPath={`/company/`}
                    sidePeekIsOpen={sidePeekIsOpen}
                />
            </div>

            <div className='flex relative items-center' id="BarChartContainer"> 
                <div className="flex-grow">               
                    <StackedBarChart
                        title={`${data.official.givenName}'s meetings with lobbyists`}
                        data={data.timelineChart}
                        profileType={'PUBLIC_SERVANT'}
                        sidePeekIsOpen={sidePeekIsOpen}
                    />
                </div>
            </div>
           
            <div className="my-10">
                <PublicServantConnections
                    publicServant={data.official.givenName}
                    connections={data.relationships}
                    totalRelationships={data.totalRelationships}
                    sidePeekIsOpen={sidePeekIsOpen}
                    contextName={name}
                />
            </div>
            <div className="mt-8">
                <h2 className={PAGE_H2}>
                    Meetings
                </h2>
                <Timeline 
                    timeline={data.timeline} 
                    totalTimeline={data.totalTimeline}
                    template="PUBLIC_SERVANT"
                    showCompany
                    showLobbyist
                    showConsultancy
                    onFetchMore={onFetchMore}
                />
            </div>
            <Footer />
        </div>
    )

}

export default PublicServantProfile;